import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toPng } from "html-to-image";
import { ArrowLeft, IdentificationCard, DownloadSimple, Calendar, EnvelopeSimple, Phone, MapPin, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";
import MemberCard from "@/components/MemberCard";
import { logger } from "@/lib/logger";

const DEFAULT_CFG = {
  club_name: "Entradas Deportivas",
  card_subtitle: "Carné Digital",
  primary_color: "#007AFF",
  secondary_color: "#0A2D5C",
  card_text_color: "#FFFFFF",
  logo_url: null,
  secondary_logo_url: null,
  background_image_url: null,
  show_photo: true,
  show_dni: true,
  card_layout: null,
};

export default function SocioDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [history, setHistory] = useState({ accesses: [], payments: [] });
  const [matchesById, setMatchesById] = useState({});
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [downloading, setDownloading] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [m, h, c, ms] = await Promise.all([
          api.get(`/members/${id}`),
          api.get(`/members/${id}/history`),
          api.get(`/club-config`).catch(() => ({ data: DEFAULT_CFG })),
          api.get(`/matches`).catch(() => ({ data: [] })),
        ]);
        setMember(m.data);
        setHistory(h.data);
        setCfg({ ...DEFAULT_CFG, ...c.data });
        const map = {};
        (ms.data || []).forEach((mm) => { map[mm.id] = mm; });
        setMatchesById(map);
      } catch {
        toast.error("No se pudo cargar el socio");
      }
    })();
  }, [id]);

  if (!member) return <div className="text-slate-500">Cargando...</div>;

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      // 1) Pre-cargar EXPLÍCITAMENTE los pesos de Barlow Condensed que usa el carné
      //    (sin esto html-to-image puede caer a una fuente de respaldo más ancha)
      const fontPromises = [
        '800 56px "Barlow Condensed"',
        '800 26px "Barlow Condensed"',
        '800 22px "Barlow Condensed"',
        '800 20px "Barlow Condensed"',
        '700 22px "Barlow Condensed"',
        '700 18px "Barlow Condensed"',
        '700 12px "Barlow Condensed"',
        '700 10px "Barlow Condensed"',
        '700 9px "Barlow Condensed"',
        '500 12px "Barlow Condensed"',
      ].map((f) => document.fonts.load(f).catch(() => null));
      await Promise.all(fontPromises);
      if (document.fonts && document.fonts.ready) await document.fonts.ready;
      await new Promise((r) => setTimeout(r, 150));

      // 2) Captura PNG a alta resolución (4×) con html-to-image
      //    A 4× obtenemos ~2000×1260 px → calidad excelente para impresoras de tarjetas (300-600 dpi).
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 4,
        cacheBust: true,
      });

      const link = document.createElement("a");
      link.download = `carne-${member.numero_socio}-${member.apellidos.toLowerCase().replace(/\s+/g, "-")}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Carné descargado");
    } catch (e) {
      logger.error(e);
      toast.error("No se pudo generar el carné");
    } finally {
      setDownloading(false);
    }
  };

  const downloadReceipt = async (index) => {
    setDownloadingReceipt(true);
    try {
      const params = {};
      if (index !== undefined && index !== null) params.index = index;
      const { data, headers } = await api.get(`/members/${member.id}/receipt.pdf`, { params, responseType: "blob" });
      const blob = new Blob([data], { type: headers["content-type"] || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const match = (headers["content-disposition"] || "").match(/filename="?([^";]+)"?/);
      a.download = match ? match[1] : `recibo_${member.numero_socio}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Recibo descargado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el recibo");
    } finally {
      setDownloadingReceipt(false);
    }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm">
        <ArrowLeft size={16} /> Volver
      </button>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Carné digital */}
        <div>
          <div className="flex items-center gap-2 mb-3 text-slate-500">
            <IdentificationCard size={18} />
            <span className="text-[10px] tracking-[0.25em] uppercase font-bold">Carné Digital</span>
          </div>
          <MemberCard cfg={cfg} socio={member} scale={1} refForward={cardRef} />
          <button
            onClick={downloadCard}
            disabled={downloading}
            data-testid="download-qr-btn"
            className="mt-3 w-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-4 h-11 text-sm flex items-center justify-center gap-2 font-display font-bold uppercase tracking-wider transition disabled:opacity-50 shadow-md shadow-blue-500/20"
            style={{ borderRadius: 8 }}
          >
            <DownloadSimple size={16} weight="bold" /> {downloading ? "Generando..." : "Descargar carné (PNG)"}
          </button>
        </div>

        {/* Info */}
        <div className="space-y-4">
          <div className="bg-white border border-slate-200 p-5 shadow-card" style={{ borderRadius: 12 }}>
            <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold mb-3">Contacto</div>
            <div className="space-y-2 text-sm">
              {member.email && <Row icon={EnvelopeSimple}>{member.email}</Row>}
              {member.telefono && <Row icon={Phone}>{member.telefono}</Row>}
              {member.direccion && <Row icon={MapPin}>{member.direccion}</Row>}
              {member.fecha_nacimiento && <Row icon={Calendar}>{member.fecha_nacimiento}</Row>}
              {!member.email && !member.telefono && !member.direccion && !member.fecha_nacimiento && (
                <div className="text-slate-400 text-sm">Sin datos de contacto</div>
              )}
            </div>
          </div>
          <div className="bg-white border border-slate-200 p-5 shadow-card" style={{ borderRadius: 12 }}>
            <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold mb-3">Estado</div>
            <div className="font-display font-bold text-3xl uppercase">{member.estado}</div>
            <div className="text-xs text-slate-500 mt-2">Tipo: <span className="text-slate-900 capitalize font-medium">{member.tipo_socio}</span></div>
            <div className="text-xs text-slate-500">Alta: {member.fecha_alta?.slice(0, 10)}</div>
          </div>
        </div>
      </div>

      {/* Historial */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold mb-3">Historial de Movimientos</h3>
          <div className="bg-white border border-slate-200 shadow-card" style={{ borderRadius: 12 }}>
            {history.accesses.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">Sin movimientos registrados</div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
                {history.accesses.map((a) => {
                  const dir = a.direction || "in";
                  const isEntry = dir === "in";
                  const isRed = a.result === "rojo";
                  const isNaranja = a.result === "naranja";
                  const badgeColor = isRed
                    ? "bg-[#EF4444]/10 text-[#EF4444]"
                    : isNaranja
                    ? "bg-[#F97316]/10 text-[#F97316]"
                    : isEntry
                    ? "bg-[#22C55E]/10 text-[#22C55E]"
                    : "bg-[#F97316]/10 text-[#F97316]";
                  const label = isRed
                    ? "Denegado"
                    : isNaranja
                    ? "Aviso"
                    : isEntry
                    ? "Entrada"
                    : "Salida";
                  const rival = matchesById[a.match_id]?.rival;
                  const ts = new Date(a.timestamp);
                  return (
                    <div key={a.id} className="px-4 py-3 flex items-center justify-between text-sm gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          data-testid={`access-badge-${dir}`}
                          className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${badgeColor}`}
                        >
                          {label}
                        </span>
                        <div className="min-w-0">
                          <div className="text-slate-800 text-xs truncate">
                            {rival ? `vs ${rival}` : (a.reason || "—")}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {ts.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" })}
                          </div>
                        </div>
                      </div>
                      <span className="font-display font-bold text-base tabular-nums whitespace-nowrap">
                        {ts.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div>
          <h3 className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold mb-3">Historial de Pagos</h3>
          <div className="bg-white border border-slate-200 shadow-card" style={{ borderRadius: 12 }}>
            {history.payments.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">Sin pagos registrados</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {history.payments.map((p) => (
                  <div key={p.id} className="px-4 py-3 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium capitalize">{p.tipo}</div>
                      <div className="text-xs text-slate-500">{p.temporada} • {p.fecha?.slice(0, 10)}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-bold text-lg">{p.importe} €</div>
                      <div className={`text-[10px] uppercase tracking-widest font-bold ${p.estado === "pagado" ? "text-[#22C55E]" : "text-[#F97316]"}`}>{p.estado}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recibos de cuota (partial_payments) */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-bold flex items-center gap-2">
            <Receipt size={14} weight="bold" /> Recibos de cuota
          </h3>
          <button
            data-testid="download-global-receipt-btn"
            onClick={() => downloadReceipt(null)}
            disabled={downloadingReceipt || !(member.partial_payments || []).length}
            className="bg-[#0A2D5C] hover:bg-[#082246] disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 h-9 text-xs flex items-center gap-2 font-display font-bold uppercase tracking-widest transition"
            style={{ borderRadius: 8 }}
            title="Descarga un PDF con TODAS las cuotas cobradas al socio"
          >
            <DownloadSimple size={14} weight="bold" /> Recibo global
          </button>
        </div>
        <div className="bg-white border border-slate-200 shadow-card" style={{ borderRadius: 12 }}>
          {!(member.partial_payments || []).length ? (
            <div className="p-6 text-center text-slate-500 text-sm">Este socio aún no tiene cuotas cobradas</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {(member.partial_payments || []).map((p, idx) => (
                <div key={`${p.fecha}-${idx}`} className="px-4 py-3 flex items-center justify-between text-sm gap-3">
                  <div>
                    <div className="font-medium">Cuota #{idx + 1}</div>
                    <div className="text-xs text-slate-500">{(p.fecha || "").slice(0, 10) || "Sin fecha"}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="font-display font-bold text-lg">{Number(p.importe || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
                    <button
                      data-testid={`download-receipt-${idx}`}
                      onClick={() => downloadReceipt(idx)}
                      disabled={downloadingReceipt}
                      className="border border-[#0A2D5C]/30 hover:border-[#0A2D5C] hover:bg-[#0A2D5C]/5 text-[#0A2D5C] px-3 h-9 text-[11px] flex items-center gap-1 font-display font-bold uppercase tracking-widest transition disabled:opacity-40"
                      style={{ borderRadius: 8 }}
                    >
                      <DownloadSimple size={12} weight="bold" /> Recibo
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-3 text-slate-700">
      <Icon size={16} className="text-slate-400" />
      <span>{children}</span>
    </div>
  );
}
